import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { InternalSingleNoteState, SaveStatus } from '../models';
import { SingleNote } from "@textbook-api-client/lib/interface/single-note.model";
import { map } from "rxjs/operators";

@Injectable()
export class ActionNotesService implements OnDestroy {
  private readonly savedNotesState$ubject = new BehaviorSubject<InternalSingleNoteState[]>([]);
  private readonly currentNotesState$ubject = new BehaviorSubject<InternalSingleNoteState[]>([]);
  private readonly saveActionNoteStatus$ = new BehaviorSubject<SaveStatus>(SaveStatus.Create);

  readonly notesEdited$ = combineLatest([
    this.savedNotesState$ubject.asObservable(),
    this.currentNotesState$ubject.asObservable(),
  ]).pipe(map(([savedNotes, currentNotes]) => !this.isEqual(savedNotes, currentNotes)));

  isNotesEditionPresent(): boolean {
    return !this.isEqual(this.savedNotesState$ubject.getValue(), this.currentNotesState$ubject.getValue());
  }

  get currentNotes(): InternalSingleNoteState[] {
    return this.currentNotesState$ubject.getValue();
  }

  get saveActionNoteStatus(): SaveStatus {
    return this.saveActionNoteStatus$.getValue();
  }

  getSaveActionNoteStatus$(): Observable<SaveStatus> {
    return this.saveActionNoteStatus$.asObservable();
  }

  currentNotesState$(): Observable<InternalSingleNoteState[]> {
    return this.currentNotesState$ubject.asObservable();
  }

  setSaveActionNoteStatus(status: SaveStatus): void {
    this.saveActionNoteStatus$.next(status);
  }

  initWithLoadedNotes(notes: InternalSingleNoteState[]): void {
    this.savedNotesState$ubject.next(notes.map((internalNote) => ({
      ...internalNote,
      note: { ...internalNote.note }
    })));
    this.updateNoteState(notes);
  }


  updateNoteState(notes: InternalSingleNoteState[]): void {
    this.currentNotesState$ubject.next(notes);
  }

  createNote(note: InternalSingleNoteState): void {
    this.currentNotesState$ubject.next([...this.currentNotesState$ubject.value, note]);
  }

  updateNote(noteToUpdate: InternalSingleNoteState): void {
    this.currentNotesState$ubject.next(
      this.currentNotesState$ubject.value.map((note: InternalSingleNoteState) => {
        return note.id === noteToUpdate.id ? noteToUpdate : note;
      })
    );
  }

  deleteNote(noteIdToRemove: string): void {
    this.currentNotesState$ubject.next(
      this.currentNotesState$ubject.value.filter(
        (note: InternalSingleNoteState) => note.id !== noteIdToRemove
      )
    );
  }

  clear(): void {
    return this.currentNotesState$ubject.next([]);
  }

  ngOnDestroy(): void {
    this.currentNotesState$ubject.complete();
    this.saveActionNoteStatus$.complete();
    this.savedNotesState$ubject.complete();
  }

  private isEqual(savedNotes: InternalSingleNoteState[], currentNotes: InternalSingleNoteState[]): boolean {
    return savedNotes.length === currentNotes.length
      && savedNotes.every((savedNote) =>
        currentNotes.some((currentNote) => this.isNoteEqual(savedNote.note, currentNote.note))
      );
  }

  private isNoteEqual(first: SingleNote, second: SingleNote): boolean {
    return first.content === second.content
      && first.distanceFromLeft === second.distanceFromLeft
      && first.distanceFromTop === second.distanceFromTop
      && first.fontColor === second.fontColor
      && first.fontSize === second.fontSize;
  }
}
